import React from 'react';
import { connect } from 'react-redux';
import { motion } from 'framer-motion';

import { camelize } from '../../util/string';
import { validProps } from '../PageBuilder/Field';
import { ASSET_NAME } from './AboutPage.duck';

import LayoutSingleColumn from '../../components/LayoutComposer/LayoutSingleColumn/LayoutSingleColumn';
import NamedLink from '../../components/NamedLink/NamedLink';
import Page from '../../components/Page/Page';
import { SecurePaymentsBadge, SvgIcon } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import VideoSection from '../LandingPage/ComingSoonSocialPage/components/VideoSection';

import pricesIcon from '../../assets/prices.svg';
import ticketIcon from '../../assets/Ticket.svg';
import bidsIcon from '../../assets/bidshand.svg';
import linkupIcon from '../../assets/linkup.svg';
import faceIcon from '../../assets/face.png';
import botIcon from '../../assets/bot.png';
import riseAndFallIcon from '../../assets/riseandfall.png';
import hackerIcon from '../../assets/hacker.png';
import useShieldIcon from '../../assets/useshield.png';

import css from './AboutPage.module.css';

const aboutPageTitle = 'About Riticket | Fair Ticket Marketplace for Real Fans';
const aboutPageDescription =
  'Learn how Riticket helps real fans buy and sell tickets with lower fees, price insights, secure payments, Crews, LinkUps, and ticket bidding.';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: index => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.62,
      delay: index * 0.08,
      ease: 'easeOut',
    },
  }),
};

const platformStats = [
  {
    value: '20',
    suffix: '%+',
    label: (
      <>
        Average savings in ticket fees when you <strong>buy on Riticket</strong>
      </>
    ),
  },
  {
    value: '10',
    suffix: '%+',
    label: (
      <>
        Average savings in ticket fees when you <strong>sell on Riticket</strong>
      </>
    ),
  },
  {
    value: '100',
    suffix: 'K+',
    label: (
      <>
        Live events tracked to <strong>connect real fans</strong> with entertainers they love
      </>
    ),
  },
  {
    value: '50',
    suffix: 'M+',
    label: (
      <>
        Ticket sales analyzed to inform <strong>price insights</strong>
      </>
    ),
  },
];

const painPoints = [
  {
    title: 'No hidden fees',
    icon: faceIcon,
  },
  {
    title: 'No bot-driven scalping',
    icon: botIcon,
  },
  {
    title: 'No price speculation',
    icon: riseAndFallIcon,
  },
  {
    title: 'No sketchy resales',
    icon: hackerIcon,
  },
];

const productSystems = [
  {
    title: 'Price insights',
    copy: 'Know when you are getting a good deal.',
    icon: pricesIcon,
  },
  {
    title: 'Fair-priced tickets',
    copy:
      'Eliminate scalping with verified Riticket listings. No markups. No hidden fees. No surprises.',
    icon: ticketIcon,
  },
  {
    title: 'Fan communities',
    copy: 'Share experiences with other real fans in Crews and IRL LinkUps.',
    icon: linkupIcon,
  },
  {
    title: 'Ticket bidding',
    copy:
      'Make offers for tickets to events you want to attend. Set your price. Sellers come to you.',
    icon: bidsIcon,
  },
  {
    title: 'Safety & security',
    copy:
      'Buy & sell with confidence through our purchase protections and payments powered by Stripe.',
    icon: useShieldIcon,
  },
];

const PriceInsightMock = () => (
  <div className={css.insightMock} aria-hidden="true">
    <div className={css.mockHeader}>
      <span>Price trend</span>
      <strong>Your Favs Tour</strong>
    </div>
    <div className={css.chartCard}>
      <svg
        viewBox="0 0 2048 1024"
        role="img"
        aria-label="Ticket price trend from 54 days ago to now"
        className={css.priceTrendChart}
      >
        <defs>
          <linearGradient
            id="aboutPriceTrendFill"
            x1="0"
            y1="94"
            x2="0"
            y2="872"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#6f55ff" stopOpacity="0.34" />
            <stop offset="0.46" stopColor="#5f4ad6" stopOpacity="0.29" />
            <stop offset="1" stopColor="#5a43d2" stopOpacity="0.50" />
          </linearGradient>
          <pattern id="aboutPriceTrendTexture" width="1" height="7" patternUnits="userSpaceOnUse">
            <path d="M0 0H2048" stroke="#302862" strokeOpacity="0.26" strokeWidth="1" />
          </pattern>
          <filter id="aboutPriceTrendGlow" x="-4%" y="-6%" width="108%" height="112%">
            <feGaussianBlur stdDeviation="2.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <clipPath id="aboutPriceTrendAreaClip">
            <path d="M136 739C156 762 180 776 211 783C239 789 267 784 290 786C316 788 334 812 359 829C386 846 410 830 433 821C458 810 482 827 508 836C537 846 561 835 582 807C606 773 620 735 646 750C688 774 719 752 749 745C784 736 826 778 852 781C881 784 896 764 914 714C927 679 938 587 951 480C966 346 981 163 1009 119C1024 96 1046 94 1059 128C1083 189 1091 328 1110 446C1129 567 1152 649 1181 681C1215 719 1242 654 1260 622C1272 600 1290 616 1305 661C1318 698 1323 750 1352 766C1375 779 1391 712 1412 684C1436 652 1480 692 1507 699C1536 706 1558 674 1587 674C1616 674 1631 728 1652 760C1675 794 1700 776 1722 781C1746 786 1758 762 1781 788C1801 811 1830 794 1854 762C1875 734 1879 688 1904 642C1927 600 1957 641 1978 679C1989 700 1995 739 1996 785L1996 872L136 872Z" />
          </clipPath>
        </defs>

        <rect width="2048" height="1024" fill="#070711" />
        <g className={css.chartGrid}>
          <path d="M119 170H2030" />
          <path d="M119 324H2030" />
          <path d="M119 476H2030" />
          <path d="M119 629H2030" />
          <path d="M119 781H2030" />
        </g>
        <g className={css.chartLabels}>
          <text x="22" y="181">
            $350
          </text>
          <text x="22" y="335">
            $300
          </text>
          <text x="22" y="487">
            $250
          </text>
          <text x="22" y="640">
            $200
          </text>
          <text x="22" y="792">
            $150
          </text>
          <text x="52" y="984">
            54 days ago
          </text>
          <text x="1942" y="984">
            Now
          </text>
        </g>
        <path
          d="M136 739C156 762 180 776 211 783C239 789 267 784 290 786C316 788 334 812 359 829C386 846 410 830 433 821C458 810 482 827 508 836C537 846 561 835 582 807C606 773 620 735 646 750C688 774 719 752 749 745C784 736 826 778 852 781C881 784 896 764 914 714C927 679 938 587 951 480C966 346 981 163 1009 119C1024 96 1046 94 1059 128C1083 189 1091 328 1110 446C1129 567 1152 649 1181 681C1215 719 1242 654 1260 622C1272 600 1290 616 1305 661C1318 698 1323 750 1352 766C1375 779 1391 712 1412 684C1436 652 1480 692 1507 699C1536 706 1558 674 1587 674C1616 674 1631 728 1652 760C1675 794 1700 776 1722 781C1746 786 1758 762 1781 788C1801 811 1830 794 1854 762C1875 734 1879 688 1904 642C1927 600 1957 641 1978 679C1989 700 1995 739 1996 785L1996 872L136 872Z"
          fill="url(#aboutPriceTrendFill)"
        />
        <rect
          width="2048"
          height="1024"
          fill="url(#aboutPriceTrendTexture)"
          clipPath="url(#aboutPriceTrendAreaClip)"
        />
        <path
          d="M136 739C156 762 180 776 211 783C239 789 267 784 290 786C316 788 334 812 359 829C386 846 410 830 433 821C458 810 482 827 508 836C537 846 561 835 582 807C606 773 620 735 646 750C688 774 719 752 749 745C784 736 826 778 852 781C881 784 896 764 914 714C927 679 938 587 951 480C966 346 981 163 1009 119C1024 96 1046 94 1059 128C1083 189 1091 328 1110 446C1129 567 1152 649 1181 681C1215 719 1242 654 1260 622C1272 600 1290 616 1305 661C1318 698 1323 750 1352 766C1375 779 1391 712 1412 684C1436 652 1480 692 1507 699C1536 706 1558 674 1587 674C1616 674 1631 728 1652 760C1675 794 1700 776 1722 781C1746 786 1758 762 1781 788C1801 811 1830 794 1854 762C1875 734 1879 688 1904 642C1927 600 1957 641 1978 679C1989 700 1995 739 1996 785"
          fill="none"
          stroke="#7c5cff"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="7"
          filter="url(#aboutPriceTrendGlow)"
        />
        <circle cx="1996" cy="785" r="20" fill="#7c5cff" />
        <circle
          cx="1996"
          cy="785"
          r="22"
          fill="none"
          stroke="#7c5cff"
          strokeOpacity="0.20"
          strokeWidth="16"
        />
      </svg>
    </div>
    <div className={css.priceMeter}>
      <div>
        <span>LOWEST</span>
        <strong>$70</strong>
      </div>
      <div>
        <span>MEDIAN</span>
        <strong>$180</strong>
      </div>
      <div>
        <span>HIGHEST</span>
        <strong>$380</strong>
      </div>
    </div>
    <div className={css.floatingBadge}>18% below avg. market price</div>
  </div>
);

const getAboutPageMetaProps = pageAssetsData => {
  // Check 'aboutPage' key (fetched by /about route's own loadData)
  // and fall back to 'about' key (fetched by CMSPage for /p/about)
  const pageData =
    pageAssetsData?.[camelize(ASSET_NAME)]?.data ||
    pageAssetsData?.about?.data;
  const meta = pageData?.meta || {};
  const title = validProps(meta.pageTitle)?.content;
  const description = validProps(meta.pageDescription)?.content;
  const socialSharing = validProps(meta.socialSharing);
  return {
    ...(title ? { title } : {}),
    ...(description ? { description } : {}),
    ...(socialSharing ? { socialSharing } : {}),
  };
};

const AboutPage = ({ pageAssetsData }) => {
  const metaProps = getAboutPageMetaProps(pageAssetsData);
  const title = metaProps.title || aboutPageTitle;
  const description = metaProps.description || aboutPageDescription;
  const socialSharing = metaProps.socialSharing || { title, description };

  return (
    <Page
      title={title}
      description={description}
      socialSharing={socialSharing}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'AboutPage',
        name: title,
        description,
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.root}>
          <section className={css.hero}>
            <div className={css.heroInner}>
              <motion.div
                className={css.heroCopy}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={0}
              >
                <span className={css.heroEyebrow}>About us</span>
                <h1>
                  Find your <span className={css.crewGradientText}>Crew</span> with{' '}
                  <span>Riticket.</span>
                </h1>
                <p>
                  Riticket is a better way to buy and sell tickets, combining safe exchanges,
                  real-time price insights and an obsession for connecting real fans.
                </p>
                <div className={css.heroActions}>
                  <NamedLink name="CrewsPage" className={css.primaryCta}>
                    Join the crowd
                  </NamedLink>
                </div>
              </motion.div>
              <motion.div
                className={css.heroVisual}
                initial={{ opacity: 0, y: 34, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.72, delay: 0.18, ease: 'easeOut' }}
              >
                <PriceInsightMock />
              </motion.div>
            </div>
          </section>

          <section className={css.featurePreview} aria-label="Riticket marketplace features">
            <div className={css.featurePreviewHeader}>
              <span className={css.eyebrow}>Built for fans</span>
            </div>
            <div className={css.statsGrid}>
              {platformStats.map((stat, index) => (
                <motion.div
                  key={stat.value}
                  className={css.statCard}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.35 }}
                  variants={fadeUp}
                  custom={index}
                >
                  <strong>
                    {stat.value}
                    <span className={css.statSuffix}>{stat.suffix}</span>
                  </strong>
                  <p>{stat.label}</p>
                </motion.div>
              ))}
            </div>
            <SecurePaymentsBadge className={css.checkoutNote} />
          </section>

          <section className={css.storySection}>
            <motion.div
              className={css.storyCopy}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.35 }}
              variants={fadeUp}
              custom={0}
            >
              <span className={css.eyebrow}>Why we exist</span>
              <h2>Born from a broken system</h2>
              <p>
                Inflated prices and widespread fraud shut out real fans and limit their support of
                teams and entertainers. Ticket scalpers and scammers cost fans and entertainers over{' '}
                <strong className={css.impactStat}>$1.5B each year.</strong>
              </p>
              <blockquote>
                Our mission is to bring fairness and transparency to the live event resale ticket
                market.
              </blockquote>
              <p>
                So, we built Riticket - a fan-centric social platform where ticket exchanges happen
                in trusted fan groups, and every transaction builds community.
              </p>
            </motion.div>

            <div className={css.problemStack}>
              {painPoints.map((point, index) => (
                <motion.article
                  key={point.title}
                  className={css.problemCard}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.35 }}
                  variants={fadeUp}
                  custom={index}
                >
                  <span>
                    <img src={point.icon} alt="" />
                  </span>
                  <div>
                    <h3>{point.title}</h3>
                  </div>
                </motion.article>
              ))}
            </div>
          </section>

          <section className={css.productSection}>
            <div className={css.productIntro}>
              <span className={css.eyebrow}>What we do</span>
              <h2>
                Riticket puts <span>real fans</span> back in control
              </h2>
              <p>
                With Riticket, you can buy and sell tickets with confidence, knowing you are part of
                a like-minded community.
              </p>
            </div>

            <div className={css.productGrid}>
              {productSystems.map((system, index) => (
                <motion.article
                  key={system.title}
                  className={css.productCard}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeUp}
                  custom={index}
                >
                  {system.title === 'Safety & security' ? (
                    <img src={system.icon} alt="" className={css.productCardShieldIcon} />
                  ) : (
                    <SvgIcon src={system.icon} alt="" className={css.productCardIcon} />
                  )}
                  <span className={css.productNumber}>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{system.title}</h3>
                  <p>{system.copy}</p>
                </motion.article>
              ))}
            </div>
          </section>

          <section className={css.ctaSection}>
            <div>
              <span className={css.eyebrow}>FANDOM, REIMAGINED</span>
              <h2>
                <span className={css.ctaLine}>They treat tickets like commodities.</span>
                <span className={css.ctaLine}>
                  We treat them like <span className={css.ctaGradientText}>social identity.</span>
                </span>
              </h2>
              <NamedLink name="CrewsPage" className={css.primaryCta}>
                Join the crowd
              </NamedLink>
            </div>
          </section>

          <VideoSection className={css.aboutVideoSection} />
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { pageAssetsData } = state.hostedAssets || {};
  return { pageAssetsData };
};

export default connect(mapStateToProps)(AboutPage);
